import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { historicalFocusDate } from '@/lib/exchange-time'
import { isPanelHistoryAnchored, isPanelDeviatedFromRecord } from './panel-history-mode'
import {
  MosaicWithoutDragDropContext as _MosaicWithoutDragDropContext,
  MosaicWindow as _MosaicWindow,
  type MosaicNode,
  type MosaicBranch,
  getLeaves,
  getPathToCorner,
  getNodeAtPath,
  getOtherDirection,
  updateTree,
  createRemoveUpdate,
  Corner,
} from 'react-mosaic-component'
import type { ComponentType as ReactComponentType } from 'react'

// React 19's stricter JSX.ElementType rejects react-mosaic-component's class
// components because @types/react-mosaic-component hasn't shipped React 19
// signatures (the lib is barely maintained — see also the unmet peer-react
// warning at install time). Cast at the import boundary; runtime is
// unchanged. The previous ``<string>`` generic at the JSX call site is dropped
// since the cast doesn't carry the generic forward; props still flow through.
// React's ComponentType is renamed to dodge the local workspace ``ComponentType``
// (string union of panel types) declared a few lines down.
const MosaicWithoutDragDropContext = _MosaicWithoutDragDropContext as unknown as ReactComponentType<
  Record<string, unknown>
>
const MosaicWindow = _MosaicWindow as unknown as ReactComponentType<Record<string, unknown>>
import type {
  ComponentType,
  GridCell,
  LayoutMode,
  ShareScope,
  TabGroup,
} from '@/types/schemas/workspace'
import { DEFER_PANEL_MOUNT } from '../panel-load-method'
import { useNotificationStore } from '@/stores/notification-store'
import { Kbd } from '@/components/ui/kbd'
import { trackEvent, EVENTS } from '@/services/analytics'
import { LayoutStoreProvider } from '@/workspace/layout-store/context'
import { useLayoutStore, useLayoutStoreApi } from '@/workspace/layout-store/hooks'
import { useChatStore } from '@/stores/chat-store'
import { resolvePanelTitle } from '@/workspace/panel-title'
import { WorkspaceStoreContext } from '@/workspace/workspace-store/hooks'
import { usePanelMode } from '@/lib/panel-mode'
import { useDrillSourcePanelIds } from '@/features/history/hooks/use-drill-source-panel-ids'
import { buildProfileExport, generateShareUrl } from '../layout-export'
import type { LayoutStoreConfig } from '@/workspace/layout-store/store'
import { collectLeaves, removeGridCell } from '@/workspace/layout-store/store'
import { useAlertPanelViewCleanup } from './use-alert-panel-view-cleanup'
import type { PanelComponent, PanelSeed, WindowConfig, LinkMode } from '../types'
import { recordChartOpenIfChartTickerChanged } from '../record-chart-open'
import { PanelWrapper } from './panel-wrapper'
import { PanelHeader } from './panel-header'
import { WorkspaceSkeleton } from './workspace-skeleton'
import { useExpressionLibrariesReady } from '@/features/expressions/store/use-expression-library'
import {
  applyDateBroadcast,
  clearLinkedTickers,
  pinDayOnLock,
  rejoinOnUnlock,
  seedSymbolIndex,
} from './link-targets'
import { linkedFocusDate } from './focus-date-sources'
import { PlacementOverlay } from './placement-overlay'
import { PanelHub } from './panel-hub'
import { WORKSPACE_MOSAIC_ID } from './panel-hub-constants'
import { resolveDrop, splitRoot, type DropPosition } from './resolve-drop'
import { PaneTabStrip } from './pane-tab-strip'
import { DASHBOARD_TYPES } from './add-panel-menu-constants'
import { useRolledOutPanelTypes } from '../panel-rollout'
import { AddPanelMenu } from './add-panel-menu'
import type { PlacementDirection } from './placement-overlay'
import { COMPONENT_META, PANEL_LIMITS } from './add-panel-menu-constants'
import { MobileWorkspace } from './mobile-workspace'
import { askAIFor } from './panel-ask-ai'
import { GridWorkspace } from './grid-workspace'
import { ShareLinkDialog } from './share-link-dialog'

/** Panel-scoped actions handed to a grid header override. */
export interface GridHeaderActions {
  /** Fire one of the panel's registered toolbar actions (e.g. 'aggregates'). */
  triggerPanelAction: (actionId: string) => void
  onDelete: () => void
  onExport: () => void
  onDuplicate: () => void
  /** Open the chat drawer with this panel already added as a mention. Absent when the host vetoes Ask AI for the type. */
  onAskAI?: () => void
  /** Which renderer is asking. Lets an override behave per layout — e.g. the
   *  scorecard always shows its title header in scroll, but opt-in in mosaic. */
  layoutMode: 'mosaic' | 'grid'
}
import { useDevice } from '@/context/device-hooks'

import 'react-mosaic-component/react-mosaic-component.css'
import './mosaic-overrides.css'

import { WorkspaceMosaicContext } from './workspace-mosaic-hooks'
import { isResizeOnly, lockedStoreMode, type LockedPersistence } from '../locked-persistence'
import type { WorkspaceMosaicContextValue } from './workspace-mosaic-hooks'

// ─── Panel toolbar wrapper — handles header-visible collapse ───

/**
 * Wraps the PanelHeader (passed as `children`) in a collapsible toolbar slot.
 *
 * When `headerVisible` is false, the toolbar collapses to a 6px drag-grip
 * strip. Hovering that strip reveals the full 30px header as a z-index:20
 * absolute overlay so the user can still drag, delete, and re-show the bar.
 * React-mosaic's drag source attaches to the toolbar container itself (not the
 * rendered content), so a 6px toolbar is still draggable.
 */
function PanelToolbarWrapper({
  headerVisible,
  children,
}: {
  headerVisible?: boolean
  children: React.ReactNode
}) {
  const [gripHovered, setGripHovered] = useState(false)
  // Live mirror of the pointer being over the strip/reveal, readable from the
  // popper-watcher below without re-arming it.
  const pointerInsideRef = useRef(false)
  // Watches for an open Radix dropdown (portaled to <body>) to unmount before
  // hiding the reveal — dropdowns spawned FROM the reveal live outside it in
  // the DOM, so mouseleave alone would hide the bar under an open menu.
  const popperWatchRef = useRef<MutationObserver | null>(null)

  const hideReveal = useCallback(() => {
    popperWatchRef.current?.disconnect()
    popperWatchRef.current = null
    setGripHovered(false)
  }, [])
  useEffect(() => () => popperWatchRef.current?.disconnect(), [])

  // Radix poppers (period/type/ƒx/templates/⋯ menus) AND the ticker search's
  // custom results portal — both render outside the strip's subtree.
  const hasOpenPopper = () =>
    document.querySelector('[data-radix-popper-content-wrapper], [data-ticker-search-portal]') !=
    null

  const handleGripLeave = useCallback(() => {
    pointerInsideRef.current = false
    if (!hasOpenPopper()) {
      hideReveal()
      return
    }
    // A dropdown from the reveal is open (period/type/ƒx/templates/⋯ menus
    // portal to <body>) — keep the bar up. Hide once the dropdown unmounts,
    // unless the pointer has come back over the strip by then.
    popperWatchRef.current?.disconnect()
    const mo = new MutationObserver(() => {
      if (!hasOpenPopper() && !pointerInsideRef.current) hideReveal()
    })
    mo.observe(document.body, { childList: true })
    popperWatchRef.current = mo
  }, [hideReveal])

  // During an HTML5 drag (panel re-docking) no mouseleave ever fires, which
  // used to leave the reveal stuck open after the drop — hide when the drag
  // finishes (dragend/drop) or the window blurs. Deliberately NOT on
  // dragstart: the reveal itself is a drag handle, and hiding the drag's
  // origin element during dragstart makes Chrome cancel the drag outright.
  useEffect(() => {
    if (!gripHovered) return
    document.addEventListener('dragend', hideReveal, true)
    document.addEventListener('drop', hideReveal, true)
    window.addEventListener('blur', hideReveal)
    return () => {
      document.removeEventListener('dragend', hideReveal, true)
      document.removeEventListener('drop', hideReveal, true)
      window.removeEventListener('blur', hideReveal)
    }
  }, [gripHovered, hideReveal])

  if (headerVisible !== false) {
    return (
      <div className="flex w-full items-center" style={{ height: 26, minHeight: 26 }}>
        {children}
      </div>
    )
  }

  // Collapsed state: 6px grip strip
  return (
    <div
      data-testid="header-grip"
      style={{ position: 'relative', height: 6, minHeight: 6, width: '100%' }}
      onMouseEnter={() => {
        pointerInsideRef.current = true
        popperWatchRef.current?.disconnect()
        popperWatchRef.current = null
        setGripHovered(true)
      }}
      onMouseLeave={handleGripLeave}
    >
      {/* Visual grip indicator */}
      <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <div className="rounded bg-border" style={{ width: 24, height: 2, opacity: 0.5 }} />
      </div>
      {/* Hover-reveal overlay — always in DOM (display toggled) so MosaicWindow
          drag internals that inspect children don't see a remount. */}
      <div
        data-testid="header-reveal"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          // Match the regular panel toolbar (28px, accent background) so the
          // reveal reads as the same chrome, just transient.
          height: 28,
          zIndex: 20,
          // Hide via visibility (not display) so the reveal stays laid out while
          // hidden. A display:none trigger collapses to zero size, which makes
          // Radix menus/popovers inside it (e.g. the more-menu) anchor to the
          // top-left (0,0) when opening flips gripHovered → false.
          visibility: gripHovered ? 'visible' : 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
        className="bg-accent border-b border-border shadow-sm"
      >
        {children}
      </div>
    </div>
  )
}

// ─── ID generation ───

function generatePanelId(): string {
  return `pnl_${crypto.randomUUID()}`
}

// Walk the mosaic tree to find the MosaicBranch[] path to a given leaf ID
function findLeafPath(
  node: MosaicNode<string>,
  targetId: string,
  path: MosaicBranch[] = [],
): MosaicBranch[] | null {
  if (typeof node === 'string') {
    return node === targetId ? path : null
  }
  return (
    findLeafPath(node.first, targetId, [...path, 'first']) ??
    findLeafPath(node.second, targetId, [...path, 'second'])
  )
}

// ─── Props ───

export interface WorkspaceMosaicProps {
  workspaceId: string
  layoutId: string
  registry: Record<ComponentType, PanelComponent>
  symbols: string[]
  onSymbolsChange?: (symbols: string[]) => void
  defaultMosaicTree?: MosaicNode<string> | null
  defaultWindowConfigs?: Record<string, WindowConfig>
  /** Default pane tab-groups (e.g. from a preset). Keyed by host leaf panel ID. */
  defaultTabGroups?: Record<string, TabGroup>
  /**
   * Default renderer for a fresh layout (e.g. a grid preset). Absent = 'mosaic'.
   * When 'grid' with no `defaultGridLayout`, the grid renderer self-heals an
   * empty arrangement into full-width stacked cells (reconcileGridCells).
   */
  defaultLayoutMode?: LayoutMode
  /** Optional explicit grid arrangement seeded alongside `defaultLayoutMode`. */
  defaultGridLayout?: GridCell[]
  /** Grid mode: cells size themselves to their content and are fixed in place. */
  gridFitContent?: boolean
  /** Grid mode: cells keep their set heights and never drag or resize (PresetLayout.gridFixed). */
  gridFixed?: boolean
  /**
   * Grid mode: a panel that scrolls inside sits behind a click-to-interact
   * cover until pressed, so the wheel scrolls the page past it. On by default;
   * the History page turns it off (Chris, 2026-10-27), since its panels take
   * the first click and Records ends its list so the wheel reaches the page.
   */
  gridClickToInteract?: boolean
  /**
   * Default per-panel states (e.g. from a preset's `panels` field). Seeds the
   * layout-store's `panels` map; persisted user states merge on top per key.
   */
  defaultPanels?: Record<string, Record<string, unknown>>
  defaultComponentType?: ComponentType
  /** Component types offered when adding a tab from a pane's strip (defaults to dashboard set). */
  availableTypes?: ComponentType[]
  /**
   * Starting state for a panel the user adds with none of its own (Panel Hub
   * click or drag, a pane's add-tab). Absent = new panels open bare. Page-owned
   * because the right default is the page's call: History's chart is not the
   * dashboard's.
   */
  panelSeed?: PanelSeed
  /**
   * Draw the Panel Hub's cards under category headers. Off for the dashboard
   * (13 pictures scan faster flat); the History page turns it on for its
   * three real groups over eighteen types.
   */
  panelHubGrouped?: boolean
  /** Render extra toolbar content per panel (e.g. period selector when chart toolbar hidden) */
  renderToolbarExtras?: (
    panelId: string,
    config: WindowConfig,
    onAction?: (actionId: string) => void,
  ) => ReactNode
  /** Render extra toolbar content on the right side of the panel header, before action buttons. */
  renderToolbarExtrasRight?: (
    panelId: string,
    config: WindowConfig,
    onAction?: (actionId: string) => void,
  ) => ReactNode
  /**
   * Override for the panel menu's "Ask AI". History passes the focus gallery;
   * the default opens the chat drawer with the panel mentioned.
   */
  onAskAI?: (panelId: string, componentType: string) => void
  /**
   * Host veto for the panel menu's "Ask AI": return false for a type that has
   * no AI (History's static scorecard) and its menu drops the item.
   */
  canAskAI?: (componentType: string) => boolean
  /**
   * Grid (scroll) mode: replace the standard PanelHeader for specific panel
   * types (return null to fall back). Lets feature pages supply minimal
   * headers (e.g. the history scorecard) without this layer importing them.
   */
  renderGridHeaderOverride?: (
    panelId: string,
    config: WindowConfig,
    actions: GridHeaderActions,
    /** The header this layer would render by default — an override returns it
     *  (or wraps it) when it decides NOT to substitute its own header, so the
     *  decision can depend on panel state the plain override function can't read. */
    defaultHeader: ReactNode,
  ) => ReactNode | null
  /** When true, the mosaic structure is frozen: panels cannot be dragged, added, or removed. */
  locked?: boolean
  /**
   * Where a locked layout's panel settings are saved: `'browser'` (default,
   * IndexedDB only) or `'server'` (the user's profile). See locked-persistence.ts.
   */
  lockedPersistence?: LockedPersistence
  /**
   * Promise from the workspace controller's bootstrap fetch. When the
   * resolved bootstrap covers this layoutId, the inner store skips
   * loadLayout/loadTemplates and trusts the IDB doc the controller wrote.
   */
  bootstrapPromise?: Promise<import('../types').BootstrapData | null> | null
  /**
   * Optional rail rendered to the LEFT of the content area, below `children`
   * (the page header). History passes its layout sidebar here so the header
   * spans the full page width while the rail scopes to the workspace body.
   * Not rendered on mobile.
   */
  leftRailSlot?: ReactNode
  /**
   * Optional block rendered INSIDE the content column, above the tiles and to
   * the right of `leftRailSlot` (unlike `children`, which span the rail too).
   * The Backtester Run page puts its static key-metric tiles here.
   */
  aboveContentSlot?: ReactNode
  /**
   * Grid mode only: put `aboveContentSlot` inside the scrolling area, as the
   * top of the page, instead of pinned above it. Off by default.
   */
  aboveContentScrolls?: boolean
  children?: ReactNode
}

// ─── Thin Wrapper (public API, same props interface) ───

export function WorkspaceMosaic({
  workspaceId,
  layoutId,
  registry,
  symbols,
  onSymbolsChange,
  defaultMosaicTree = null,
  defaultWindowConfigs = {},
  defaultTabGroups,
  defaultPanels,
  defaultLayoutMode,
  defaultGridLayout,
  gridFitContent = false,
  gridFixed = false,
  gridClickToInteract = true,
  defaultComponentType = 'chart',
  availableTypes = DASHBOARD_TYPES,
  panelSeed,
  panelHubGrouped = false,
  renderToolbarExtras,
  renderToolbarExtrasRight,
  renderGridHeaderOverride,
  onAskAI: onAskAIOverride,
  canAskAI,
  locked = false,
  lockedPersistence,
  bootstrapPromise,
  leftRailSlot,
  aboveContentSlot,
  aboveContentScrolls,
  children,
}: WorkspaceMosaicProps) {
  // Optional — research-layout mounts WorkspaceMosaic without a workspace
  // store provider, in which case we skip name propagation entirely.
  const workspaceStore = useContext(WorkspaceStoreContext)
  const storeConfig: LayoutStoreConfig = {
    workspaceId,
    layoutId,
    defaults: {
      mosaicTree: defaultMosaicTree,
      windowConfigs: defaultWindowConfigs,
      tabGroups: defaultTabGroups,
      layoutMode: defaultLayoutMode,
      gridLayout: defaultGridLayout,
      gridFixed,
      panels: defaultPanels,
    },
    ...lockedStoreMode(locked, lockedPersistence),
    bootstrapPromise,
    // Every save reads the current tree-item name and embeds it into the
    // doc. Going forward: rename → next save picks up the new name; new
    // layouts are saved with name from creation. Pre-existing docs without
    // a name field stay valid (the schema field is optional).
    getLayoutName: workspaceStore
      ? () => workspaceStore.getState().data.treeItems[layoutId]?.name ?? null
      : undefined,
  }

  return (
    <LayoutStoreProvider key={layoutId} config={storeConfig}>
      <WorkspaceMosaicContent
        workspaceId={workspaceId}
        layoutId={layoutId}
        registry={registry}
        symbols={symbols}
        onSymbolsChange={onSymbolsChange}
        defaultComponentType={defaultComponentType}
        availableTypes={availableTypes}
        panelSeed={panelSeed}
        panelHubGrouped={panelHubGrouped}
        renderToolbarExtras={renderToolbarExtras}
        renderToolbarExtrasRight={renderToolbarExtrasRight}
        renderGridHeaderOverride={renderGridHeaderOverride}
        onAskAI={onAskAIOverride}
        canAskAI={canAskAI}
        gridFitContent={gridFitContent}
        gridFixed={gridFixed}
        gridClickToInteract={gridClickToInteract}
        locked={locked}
        leftRailSlot={leftRailSlot}
        aboveContentSlot={aboveContentSlot}
        aboveContentScrolls={aboveContentScrolls}
      >
        {children}
      </WorkspaceMosaicContent>
    </LayoutStoreProvider>
  )
}

// ─── Inner Content (reads layout from context) ───

interface WorkspaceMosaicContentProps {
  workspaceId: string
  layoutId: string
  registry: Record<ComponentType, PanelComponent>
  symbols: string[]
  onSymbolsChange?: (symbols: string[]) => void
  defaultComponentType: ComponentType
  availableTypes: ComponentType[]
  panelSeed?: PanelSeed
  panelHubGrouped?: boolean
  renderToolbarExtras?: (
    panelId: string,
    config: WindowConfig,
    onAction?: (actionId: string) => void,
  ) => ReactNode
  renderToolbarExtrasRight?: (
    panelId: string,
    config: WindowConfig,
    onAction?: (actionId: string) => void,
  ) => ReactNode
  onAskAI?: (panelId: string, componentType: string) => void
  /**
   * Host veto for the panel menu's "Ask AI": return false for a type that has
   * no AI (History's static scorecard) and its menu drops the item.
   */
  canAskAI?: (componentType: string) => boolean
  renderGridHeaderOverride?: (
    panelId: string,
    config: WindowConfig,
    actions: GridHeaderActions,
    /** The header this layer would render by default — an override returns it
     *  (or wraps it) when it decides NOT to substitute its own header, so the
     *  decision can depend on panel state the plain override function can't read. */
    defaultHeader: ReactNode,
  ) => ReactNode | null
  /** Grid mode: cells size to content and are fixed in place. */
  gridFitContent?: boolean
  /** Grid mode: cells keep their set heights and never drag or resize. */
  gridFixed?: boolean
  /** Grid mode: click-to-interact cover over scrolling panels (see WorkspaceMosaicProps). */
  gridClickToInteract?: boolean
  locked?: boolean
  leftRailSlot?: ReactNode
  /**
   * Optional block rendered INSIDE the content column, above the tiles and to
   * the right of `leftRailSlot` (unlike `children`, which span the rail too).
   * The Backtester Run page puts its static key-metric tiles here.
   */
  aboveContentSlot?: ReactNode
  /**
   * Grid mode only: put `aboveContentSlot` inside the scrolling area, as the
   * top of the page, instead of pinned above it. Off by default.
   */
  aboveContentScrolls?: boolean
  children?: ReactNode
}

function WorkspaceMosaicContent({
  workspaceId,
  layoutId,
  registry,
  symbols,
  onSymbolsChange,
  defaultComponentType,
  availableTypes: offeredTypes,
  panelSeed,
  panelHubGrouped = false,
  renderToolbarExtras,
  renderToolbarExtrasRight,
  renderGridHeaderOverride,
  onAskAI: onAskAIOverride,
  canAskAI,
  gridFitContent = false,
  gridFixed = false,
  gridClickToInteract = true,
  locked = false,
  leftRailSlot,
  aboveContentSlot,
  aboveContentScrolls = false,
  children,
}: WorkspaceMosaicContentProps) {
  // A flagged type is not offered anywhere in this mosaic (Add Panel, the
  // per-pane "+" menu, Panel Hub) while its rollout flag is off.
  const availableTypes = useRolledOutPanelTypes(offeredTypes)
  const storeApi = useLayoutStoreApi()
  const { isMobile } = useDevice()

  // Subscribe to individual slices — deliberately skip mosaicTree since the
  // Mosaic is uncontrolled (it manages its own tree). We only read it once
  // for initialValue via storeApi.getState().
  const windowConfigs = useLayoutStore((s) => s.windowConfigs)
  useAlertPanelViewCleanup(windowConfigs)
  const tabGroups = useLayoutStore((s) => s.tabGroups)
  const layoutLoaded = useLayoutStore((s) => s.layoutLoaded)
  const librariesReady = useExpressionLibrariesReady()
  const layoutMode = useLayoutStore((s) => s.layoutMode)
  const shareScope: ShareScope | null =
    workspaceId === 'dashboard' || workspaceId === 'history' ? workspaceId : null

  // ─── Two-phase panel mount — OPT-IN via `?panelLoadMethod=deferred` ───
  //
  // Off by default. When on, the outline (panes, splits, sizes, headers, tab
  // strips) paints first with blank bodies and panels mount a frame later.
  // Structure appears sooner; total time to painted content is ~60% longer
  // because the subtree reconciles twice. See panel-load-method.ts.
  const [panelsReady, setPanelsReady] = useState(!DEFER_PANEL_MOUNT)
  useEffect(() => {
    if (!DEFER_PANEL_MOUNT || !layoutLoaded) return
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPanelsReady(true))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [layoutLoaded])
  const recoveryCount = useLayoutStore((s) => s.recoveryCount)
  // Empty-layout detector. Subscribes to a DERIVED boolean so it only flips on
  // null↔non-null transitions — never re-renders the mosaic on ordinary tree
  // edits (which is why mosaicTree itself is deliberately not subscribed above).
  const isEmptyLayout = useLayoutStore((s) => s.mosaicTree === null)

  const [activePanel, setActivePanel] = useState<string | null>(null)
  const activePanelRef = useRef(activePanel)
  activePanelRef.current = activePanel

  // Per-panel drill-open flags — a chart host sets these while its drill overlay
  // is open so the mosaic can frame the WHOLE window with the purple drill
  // border (keyed by the panel id the mosaic renders under).
  const drillOpen = usePanelMode((s) => s.drillOpen)
  const replayOpen = usePanelMode((s) => s.replayOpen)
  // Panel ids a header filter chip was drilled from (History page): the
  // window that owns a double-clicked bar / cell / flow keeps a primary border
  // while that chip is active. Empty off the History page.
  const drillSourceIds = useDrillSourcePanelIds()

  // Panel ids currently in HISTORY mode via their own header calendar
  // (panel-local focusDate) OR anchored to a past day by a drill (drillDate).
  // The broadcast `focusDate` prop only covers history-row clicks — without
  // this, a date picked on the panel itself never lit the yellow window border.
  //
  // `drillDate`: a shift+click drill into a PAST day writes drillDate (not
  // focusDate — that drives calendar/data-fetch semantics; drillDate is the
  // display anchor). Treating a historical drillDate as history mode is what
  // makes "yellow supersedes drill" actually fire: the window then carries BOTH
  // the drill (purple) and focus-date (yellow) classes, and CSS source order
  // lets yellow win. A button/'d' drill or a drill into TODAY leaves drillDate
  // null / non-historical, so those correctly stay purple.
  //
  // Joined string keeps the selector referentially stable across unrelated
  // store changes. historicalFocusDate: an explicit TODAY (the calendar's Today
  // button) is live, not history — no yellow border for it.
  const localFocusIdsKey = useLayoutStore((s) =>
    Object.keys(s.panels)
      .filter((id) =>
        isPanelHistoryAnchored(
          s.panels[id] as { focusDate?: string | null; drillDate?: string | null },
        ),
      )
      .sort()
      .join(','),
  )
  const localFocusIds = useMemo(() => new Set(localFocusIdsKey.split(',')), [localFocusIdsKey])

  // Focus date for chart rewind (set by TickerStats row click or chart calendar, consumed by ChartPanel)
  const [focusDate, setFocusDate] = useState<string | null>(null)
  // Tracks which component type initiated the current focus date
  const [focusDateSource, setFocusDateSource] = useState<ComponentType | null>(null)

  // History-page yellow: panel ids whose drill has navigated AWAY from the
  // reviewed record's date (see isPanelDeviatedFromRecord). The record baseline
  // is the panel's own focusDate or the broadcast `focusDate` (the clicked
  // history row; none for a locked chart, which holds its own day), so this
  // must be declared AFTER `focusDate`. The inline closure over `focusDate`
  // re-runs on re-render, keeping the broadcast date current; the
  // joined-string key keeps the set referentially stable.
  const deviatedIdsKey = useLayoutStore((s) =>
    Object.keys(s.panels)
      .filter((id) =>
        isPanelDeviatedFromRecord(
          s.panels[id] as { focusDate?: string | null; drillDate?: string | null },
          linkedFocusDate(s.windowConfigs[id], focusDate),
        ),
      )
      .sort()
      .join(','),
  )
  const deviatedIds = useMemo(() => new Set(deviatedIdsKey.split(',')), [deviatedIdsKey])

  // Export panel share URL dialog state
  const [exportUrl, setExportUrl] = useState('')
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportError, setExportError] = useState('')
  const exportRetryRef = useRef<() => void>(undefined)

  // Per-panel toolbar action handlers (registered by panel components)
  const panelActionHandlersRef = useRef<
    Record<string, (actionId: string, anchor?: { x: number; y: number }) => void>
  >({})

  // Per-panel action overrides — stored in refs to prevent mount-time re-render cascades.
  // When panels register actions during mount effects, we write to refs and schedule a
  // single deferred re-render (via rAF) so the first paint isn't blocked.
  const panelActionOverridesRef = useRef<Record<string, string[]>>({})
  const panelActionBadgesRef = useRef<Record<string, Record<string, number>>>({})
  const [, setPanelActionVersion] = useState(0)
  const pendingActionUpdate = useRef(false)

  // Notify user when panels were recovered with defaults
  useEffect(() => {
    if (layoutLoaded && recoveryCount > 0) {
      useNotificationStore.getState().showInfo(`${recoveryCount} panel(s) were reset to defaults`)
    }
  }, [layoutLoaded, recoveryCount])

  // ─── Sync symbols with globalSettings (persisted to IDB + API) ───
  const symbolsInitializedRef = useRef(false)
  useEffect(() => {
    if (!layoutLoaded) return

    if (!symbolsInitializedRef.current) {
      // First run after store loads: restore persisted symbols
      symbolsInitializedRef.current = true
      const gs = storeApi.getState().globalSettings
      const stored = gs.symbols as string[] | undefined
      if (stored && Array.isArray(stored) && stored.length > 0) {
        // Use stored list as base (indices match persisted windowConfigs).
        // Append any local-only tickers that aren't in the stored list.
        const storedSet = new Set(stored)
        const localOnly = symbols.filter((s) => !storedSet.has(s))
        const merged = localOnly.length > 0 ? [...stored, ...localOnly] : stored
        if (JSON.stringify(merged) !== JSON.stringify(symbols)) {
          onSymbolsChange?.(merged)
        }
      }
      return
    }

    // Subsequent runs: persist symbols changes to globalSettings
    const { globalSettings, mosaicTree, windowConfigs, updateLayout } = storeApi.getState()
    const stored = globalSettings.symbols as string[] | undefined
    if (JSON.stringify(stored) === JSON.stringify(symbols)) return
    updateLayout(mosaicTree, windowConfigs, { ...globalSettings, symbols })
  }, [symbols, layoutLoaded, storeApi, onSymbolsChange])

  // ─── Panel operations ───
  // Callbacks read current state via storeApi.getState() (like old ref-based code)
  // so they stay stable and don't depend on the `layout` render snapshot.

  const handleSymbolChange = useCallback(
    (windowId: string, symbolIndex: number, opts?: { skipSource?: boolean }) => {
      const { windowConfigs: configs, mosaicTree, updateLayout } = storeApi.getState()
      const sourceCfg = configs[windowId]
      if (!sourceCfg) return

      const sourceMode: LinkMode = sourceCfg.linkMode ?? 'onChange'

      // Update the source panel (re-engaging ticker if it was cleared), unless
      // this is a broadcast-only dispatch (skipSource): feed-style panels like
      // news propagate a clicked ticker to peers without re-filtering their
      // own view (the legacy app's IGNORE_SELF).
      const next: Record<string, WindowConfig> = opts?.skipSource
        ? { ...configs }
        : {
            ...configs,
            [windowId]: { ...sourceCfg, symbolIndex, tickerCleared: false },
          }
      let peersUpdated = 0

      // Broadcast to linked peers (skip if isolated)
      if (sourceMode !== 'none') {
        for (const [id, cfg] of Object.entries(configs)) {
          if (id === windowId) continue
          const targetMode: LinkMode = cfg.linkMode ?? 'onChange'

          const shouldUpdate =
            sourceMode === 'onChange'
              ? targetMode === 'onChange'
              : // color → same-color peers + all onChange panels
                targetMode === sourceMode || targetMode === 'onChange'

          if (shouldUpdate) {
            // Re-engage ticker on target panels too
            next[id] = { ...cfg, symbolIndex, tickerCleared: false }
            peersUpdated++
          }
        }
      }

      // Broadcast-only with nobody listening: nothing changed, skip the write.
      if (opts?.skipSource && peersUpdated === 0) return

      // Only user gestures reach here (chart symbol search, panel ticker clicks).
      recordChartOpenIfChartTickerChanged(configs, next)
      updateLayout(mosaicTree, next)
    },
    [storeApi],
  )

  const handleSelectTicker = useCallback(
    (windowId: string, ticker: string, date?: string | null, opts?: { skipSource?: boolean }) => {
      let idx = symbols.findIndex((s) => s.toLowerCase() === ticker.toLowerCase())

      if (idx === -1) {
        idx = symbols.length
        onSymbolsChange?.([...symbols, ticker.toUpperCase()])
      }

      const state = storeApi.getState()
      const configs = state.windowConfigs

      // Broadcast first so date + symbol land in one render. applyDateBroadcast
      // also clears panel-local focusDate on linked targets — broadcast must
      // win over a stale local override.
      if (date !== undefined) {
        applyDateBroadcast(configs, windowId, date, {
          setFocusDate,
          setFocusDateSource,
          updatePanel: state.updatePanel,
        })
      }

      // Skip symbol update if this panel already has this ticker. Not valid
      // for broadcast-only dispatches: the source config stays untouched
      // there, so its own index says nothing about whether peers need it, nor
      // after a cleared selection, which its peers still show as cleared.
      const source = configs[windowId]
      if (!opts?.skipSource && source?.symbolIndex === idx && !source.tickerCleared) return

      handleSymbolChange(windowId, idx, opts)
    },
    [symbols, onSymbolsChange, handleSymbolChange, storeApi],
  )

  // A selection that is gone (Records listed no records): the source and the
  // panels its broadcast reaches show no ticker and no day, their empty state,
  // until the next selection. A locked panel is not reached.
  const handleClearSelection = useCallback(
    (windowId: string) => {
      const state = storeApi.getState()
      const configs = state.windowConfigs
      applyDateBroadcast(configs, windowId, null, {
        setFocusDate,
        setFocusDateSource,
        updatePanel: state.updatePanel,
      })
      const next = clearLinkedTickers(configs, windowId)
      if (next !== configs) state.updateLayout(state.mosaicTree, next)
    },
    [storeApi],
  )

  const handleLinkModeChange = useCallback(
    (windowId: string, mode: LinkMode) => {
      const {
        windowConfigs: configs,
        panels,
        mosaicTree,
        updateLayout,
        updatePanel,
      } = storeApi.getState()
      // A locked chart stops following the date broadcast, so the day it shows
      // becomes its own first: before the lock lands, so no render moves it.
      pinDayOnLock(configs, panels, windowId, mode, focusDate, updatePanel)
      // Unlocking rejoins the selection, ticker and day together: the pinned day
      // goes first and the group's ticker lands with the new link mode, so no
      // render pairs one record's ticker with another record's day.
      const rejoin = rejoinOnUnlock(configs, panels, windowId, mode)
      if (rejoin) updatePanel(windowId, rejoin.panel)
      const next = {
        ...configs,
        [windowId]: {
          ...configs[windowId],
          linkMode: mode,
          ...(rejoin?.symbolIndex != null ? { symbolIndex: rejoin.symbolIndex } : {}),
        },
      }
      updateLayout(mosaicTree, next)
    },
    [storeApi, focusDate],
  )

  const handleClearTicker = useCallback(
    (windowId: string) => {
      const { windowConfigs: configs, mosaicTree, updateLayout } = storeApi.getState()
      const cfg = configs[windowId]
      if (!cfg) return
      const next = { ...configs, [windowId]: { ...cfg, tickerCleared: true } }
      updateLayout(mosaicTree, next)
    },
    [storeApi],
  )

  const handleToggleHeaderVisible = useCallback(
    (id: string) => {
      const { windowConfigs: configs, mosaicTree, updateLayout } = storeApi.getState()
      const cur = configs[id]
      if (!cur) return
      const hidingHeader = (cur.headerVisible ?? true) === true
      const next = {
        ...configs,
        [id]: {
          ...cur,
          headerVisible: !hidingHeader,
        },
      }
      updateLayout(mosaicTree, next)
    },
    [storeApi],
  )

  // Panel kebab menu "Ask AI" — opens the chat drawer with this panel already
  // added as a mention (see chat-store.ts openWithMention).
  const handleAskAI = useCallback(
    (panelId: string, componentType: string) => {
      if (onAskAIOverride) {
        onAskAIOverride(panelId, componentType)
        return
      }
      useChatStore.getState().openWithMention({
        panelId,
        componentType,
        title: resolvePanelTitle(panelId),
      })
    },
    [onAskAIOverride],
  )
  const askAIHandler = useMemo(() => askAIFor(canAskAI, handleAskAI), [canAskAI, handleAskAI])

  // ─── Header-visibility hotkeys ───
  // Option+Shift+B (toggle): hide/show ALL panels' top bars. Option+B
  // (toggle): hide/show the top bar of the panel under the cursor. Never
  // while typing; hiding re-shows chart toolbars (same companion rule as
  // handleToggleHeaderVisible). e.code: macOS Option+letter rewrites e.key
  // to a special character.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code !== 'KeyB' || !e.altKey) return
      if (e.metaKey || e.ctrlKey || e.repeat) return
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      )
        return
      const { windowConfigs: configs, mosaicTree, updateLayout } = storeApi.getState()
      if (e.shiftKey) {
        // Toggle ALL: hide everywhere unless everything is already hidden.
        const ids = Object.keys(configs)
        if (ids.length === 0) return
        e.preventDefault()
        const allHidden = ids.every((id) => configs[id]?.headerVisible === false)
        const nextVisible = allHidden
        const next = { ...configs }
        for (const id of ids) {
          next[id] = {
            ...next[id],
            headerVisible: nextVisible,
          }
        }
        updateLayout(mosaicTree, next)
        return
      }
      // Plain Option+B → the hovered panel only. [data-panel-id] sits on the
      // PanelWrapper (the BODY); hovering the panel's top bar misses it, so
      // fall back to the hovered .mosaic-window's descendant wrapper — the
      // hotkey must work from the header too.
      let hovered: HTMLElement | null = null
      try {
        hovered =
          Array.from(document.querySelectorAll<HTMLElement>('[data-panel-id]')).find((el) =>
            el.matches(':hover'),
          ) ?? document.querySelector<HTMLElement>('.mosaic-window:hover [data-panel-id]')
      } catch {
        hovered = null
      }
      const id = hovered?.getAttribute('data-panel-id')
      if (!id || !configs[id]) return
      e.preventDefault()
      handleToggleHeaderVisible(id)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [storeApi, handleToggleHeaderVisible])

  // ─── Panel Hub (visual add-panel rail) ───
  const [panelHubOpen, setPanelHubOpen] = useState(false)

  // ─── Placement mode state ───

  const [placementPending, setPlacementPending] = useState<{
    componentType: ComponentType
    initialState?: Record<string, unknown>
    /** Hide the overlay's Replace action. Duplicates set this: replacing a
     *  panel with a copy of itself is a no-op, so a duplicate must land as a
     *  split or a tab. */
    hideReplace?: boolean
  } | null>(null)

  // ─── Move mode state — relocating an existing panel via the placement overlay ───
  const [movePending, setMovePending] = useState<{
    panelId: string
    sourceHostId: string
  } | null>(null)

  // Give a panel the user just added with no state of its own the page's
  // starting state (the panel plus any chart-grid cells), marked loaded so
  // registerPanel() doesn't overwrite it. Every insert path lands here, so a
  // hub drag, a hub click, a tab add and a grid append all open the same way.
  const seedPanel = useCallback(
    (panelId: string, componentType: ComponentType) => {
      const states = panelSeed?.(componentType, panelId)
      if (!states) return
      const loaded = Object.fromEntries(Object.keys(states).map((key) => [key, true]))
      storeApi.setState((s) => ({
        panels: { ...s.panels, ...(states as typeof s.panels) },
        panelLoaded: { ...s.panelLoaded, ...loaded },
      }))
    },
    [panelSeed, storeApi],
  )

  // Core tree mutation: inserts a new panel into the mosaic tree.
  // When targetPanelId + placement are provided, splits at that panel;
  // otherwise falls back to Corner.TOP_RIGHT insertion.
  const insertPanelAtTarget = useCallback(
    (
      componentType: ComponentType,
      initialState?: Record<string, unknown>,
      targetPanelId?: string,
      placement?: PlacementDirection,
      existingPanelId?: string,
    ) => {
      if (locked) return
      if (layoutMode === 'grid') {
        const id = storeApi.getState().addGridPanel(componentType, initialState)
        if (!initialState) seedPanel(id, componentType)
        return id
      }
      // When moving an existing tab out into the tree, reuse its panel id and
      // preserve its existing windowConfig (don't seed a fresh one).
      const isExisting = existingPanelId != null
      const id = existingPanelId ?? generatePanelId()
      const { windowConfigs: configs, mosaicTree: tree, updateLayout } = storeApi.getState()
      // New panels join the onChange link group (no linkMode = onChange), so
      // they open on that group's CURRENT ticker — not symbolIndex 0, which is
      // always symbols[0] ('AAPL') regardless of what the workspace is showing.
      const withId = (base: Record<string, WindowConfig>): Record<string, WindowConfig> =>
        isExisting
          ? { ...base }
          : { ...base, [id]: { symbolIndex: seedSymbolIndex(configs), componentType } }
      const newConfigs = withId(configs)

      if (!tree) {
        updateLayout(id, newConfigs)
      } else if (targetPanelId && placement === 'replace') {
        // Replace: swap the target panel with the new one in-place. This is
        // WHOLE-pane replace — if the target hosts a tab group, close its
        // extra tabs first (cleans their configs/state and removes the group
        // entry). Swapping the leaf without this orphans the group: a dead
        // tabGroups entry whose member configs the release-guard then keeps
        // alive in the doc forever.
        const targetGroup = storeApi.getState().tabGroups[targetPanelId]
        if (targetGroup) {
          for (const tabId of targetGroup.tabs) {
            if (tabId !== targetPanelId) storeApi.getState().closeTab(targetPanelId, tabId)
          }
        }
        // Re-read after the tab cleanup — `tree`/`configs` above are stale now.
        const { mosaicTree: freshTree, windowConfigs: freshConfigs } = storeApi.getState()
        const leafPath = freshTree ? findLeafPath(freshTree, targetPanelId) : null
        if (freshTree && leafPath) {
          const update = {
            path: leafPath,
            spec: { $set: id },
          }
          const newTree = updateTree(freshTree, [
            update as Parameters<typeof updateTree<string>>[1][0],
          ])
          const { [targetPanelId]: _, ...restConfigs } = freshConfigs
          const finalConfigs = withId(restConfigs)
          updateLayout(newTree, finalConfigs)
          storeApi.getState().cleanupPanel(targetPanelId)
          delete panelActionHandlersRef.current[targetPanelId]
          const { [targetPanelId]: _o, ...restOverrides } = panelActionOverridesRef.current
          panelActionOverridesRef.current = restOverrides
        } else {
          updateLayout(freshTree ?? tree, withId(freshConfigs))
        }
      } else if (targetPanelId && placement) {
        const leafPath = findLeafPath(tree, targetPanelId)
        if (!leafPath) {
          // Fallback: target not found, insert at corner
          const path = getPathToCorner(tree, Corner.TOP_RIGHT)
          const parent = getNodeAtPath(tree, path.slice(0, -1)) as MosaicNode<string>
          const direction =
            parent && typeof parent !== 'string' ? getOtherDirection(parent.direction) : 'row'
          const update = {
            path,
            spec: {
              $set: {
                direction,
                first: getNodeAtPath(tree, path) as MosaicNode<string>,
                second: id,
              },
            },
          }
          const newTree = updateTree(tree, [update as Parameters<typeof updateTree<string>>[1][0]])
          updateLayout(newTree, newConfigs)
        } else {
          const splitDirection = placement === 'left' || placement === 'right' ? 'row' : 'column'
          const existingNode = getNodeAtPath(tree, leafPath) as MosaicNode<string>
          const first = placement === 'left' || placement === 'top' ? id : existingNode
          const second = placement === 'left' || placement === 'top' ? existingNode : id
          const update = {
            path: leafPath,
            spec: { $set: { direction: splitDirection, first, second } },
          }
          const newTree = updateTree(tree, [update as Parameters<typeof updateTree<string>>[1][0]])
          updateLayout(newTree, newConfigs)
        }
      } else if (placement && placement !== 'replace' && placement !== 'tab') {
        // Root split: a hub card was dropped on one of the mosaic's four outer
        // edges, so the new panel takes that whole side and the existing tree
        // keeps the other.
        updateLayout(splitRoot(tree, id, placement), newConfigs)
      } else {
        // Default: insert at TOP_RIGHT corner
        const path = getPathToCorner(tree, Corner.TOP_RIGHT)
        const parent = getNodeAtPath(tree, path.slice(0, -1)) as MosaicNode<string>
        const direction =
          parent && typeof parent !== 'string' ? getOtherDirection(parent.direction) : 'row'
        const update = {
          path,
          spec: {
            $set: { direction, first: getNodeAtPath(tree, path) as MosaicNode<string>, second: id },
          },
        }
        const newTree = updateTree(tree, [update as Parameters<typeof updateTree<string>>[1][0]])
        updateLayout(newTree, newConfigs)
      }

      // If initial panel state provided, set it immediately and mark as loaded
      // so registerPanel() doesn't overwrite it with API data
      if (initialState) {
        storeApi.setState((s) => ({
          panels: { ...s.panels, [id]: initialState },
          panelLoaded: { ...s.panelLoaded, [id]: true },
        }))
      } else if (!isExisting) {
        // A move keeps the panel it already has; only a new one is seeded.
        seedPanel(id, componentType)
      }
      return id
    },
    [storeApi, locked, layoutMode, seedPanel],
  )

  // Enforce per-type limits (expensive/redundant panels are capped). Shared by
  // every insert path so a drop from the hub cannot slip past what the menu
  // refuses.
  const withinStaticLimit = useCallback(
    (componentType: ComponentType): boolean => {
      const limit = PANEL_LIMITS[componentType]
      if (limit == null) return true
      const count = Object.values(storeApi.getState().windowConfigs).filter(
        (c) => c.componentType === componentType,
      ).length
      if (count < limit) return true
      useNotificationStore
        .getState()
        .showWarning(
          `Limit reached — up to ${limit} ${COMPONENT_META[componentType]?.label ?? componentType} ${
            limit === 1 ? 'panel' : 'panels'
          } per layout.`,
        )
      return false
    },
    [storeApi],
  )

  // Public addPanel: if tree is empty, insert directly; otherwise enter placement mode
  const addPanel = useCallback(
    (
      componentType: ComponentType = defaultComponentType,
      initialState?: Record<string, unknown>,
      opts?: { hideReplace?: boolean },
    ) => {
      if (locked) return
      if (!withinStaticLimit(componentType)) return
      // Scroll (grid) layout: there's no placement gesture — just append the
      // new panel to the bottom of the scroll.
      if (layoutMode === 'grid') {
        const id = storeApi.getState().addGridPanel(componentType, initialState)
        if (!initialState) seedPanel(id, componentType)
        return
      }
      const { mosaicTree: tree } = storeApi.getState()
      if (!tree) {
        insertPanelAtTarget(componentType, initialState)
      } else {
        // The placement overlay needs the whole mosaic; the hub steps aside.
        setPanelHubOpen(false)
        setPlacementPending({ componentType, initialState, hideReplace: opts?.hideReplace })
      }
    },
    [
      defaultComponentType,
      storeApi,
      locked,
      withinStaticLimit,
      insertPanelAtTarget,
      layoutMode,
      seedPanel,
    ],
  )

  // A hub card dropped on a mosaic edge. react-mosaic hands back the drop
  // target's path + side; resolve that against the store's tree (the truth)
  // and insert the same way the placement D-pad would.
  const dropPanel = useCallback(
    (componentType: ComponentType, path: MosaicBranch[], position: DropPosition): boolean => {
      if (locked || layoutMode === 'grid') return false
      if (!withinStaticLimit(componentType)) return false
      const resolution = resolveDrop(storeApi.getState().mosaicTree, path, position)
      if (resolution.kind === 'split-panel') {
        insertPanelAtTarget(
          componentType,
          undefined,
          resolution.targetPanelId,
          resolution.placement,
        )
      } else if (resolution.kind === 'split-root') {
        insertPanelAtTarget(componentType, undefined, undefined, resolution.placement)
      } else {
        insertPanelAtTarget(componentType)
      }
      return true
    },
    [locked, layoutMode, withinStaticLimit, storeApi, insertPanelAtTarget],
  )

  const togglePanelHub = useCallback(() => {
    if (locked) return
    setPanelHubOpen((open) => !open)
  }, [locked])
  const closePanelHub = useCallback(() => setPanelHubOpen(false), [])

  const handlePlacement = useCallback(
    (targetPanelId: string, direction: PlacementDirection) => {
      // ── Move mode: relocate an existing panel (a tab, or a whole pane's
      // active panel) to the chosen target ──
      if (movePending) {
        const { panelId, sourceHostId } = movePending
        const store = storeApi.getState()
        // detachPanel can re-key the source pane (host tab moved out, next tab
        // promoted to the leaf) or remove it (single-panel pane). A target on
        // the source pane must follow that new id.
        const retarget = (newSourceHost: string | null) =>
          targetPanelId === sourceHostId ? newSourceHost : targetPanelId
        if (direction === 'tab') {
          // Re-tab into the target pane's group. No-op if it's already there.
          if (targetPanelId !== sourceHostId) {
            store.detachPanel(sourceHostId, panelId)
            storeApi.getState().attachExistingTab(targetPanelId, panelId)
          }
        } else if (direction === 'replace') {
          // Replace the target pane's ACTIVE tab with the moved panel. Must go
          // through the group-aware store action — the raw insert 'replace'
          // swaps the tree leaf without touching tabGroups, which orphans the
          // target's group (dead group + member configs kept in the doc
          // forever). Precondition-check before detaching so a no-op replace
          // never leaves the moved panel detached and homeless.
          const tGroup = store.tabGroups[targetPanelId]
          const targetActive =
            tGroup && tGroup.tabs.includes(tGroup.activeTabId) ? tGroup.activeTabId : targetPanelId
          if (targetActive !== panelId && (store.windowConfigs[targetPanelId] || tGroup)) {
            const target = retarget(store.detachPanel(sourceHostId, panelId))
            const replacedId = target
              ? storeApi.getState().replaceActiveTabWithExisting(target, panelId)
              : null
            if (replacedId) {
              delete panelActionHandlersRef.current[replacedId]
              const { [replacedId]: _o, ...restOverrides } = panelActionOverridesRef.current
              panelActionOverridesRef.current = restOverrides
              const { [replacedId]: _b, ...restBadges } = panelActionBadgesRef.current
              panelActionBadgesRef.current = restBadges
            }
          }
        } else {
          // Split: take the panel out and insert it beside the target.
          const componentType = store.windowConfigs[panelId]?.componentType ?? defaultComponentType
          const target = retarget(store.detachPanel(sourceHostId, panelId))
          if (target) {
            insertPanelAtTarget(componentType, undefined, target, direction, panelId)
          }
        }
        setMovePending(null)
        return
      }

      if (!placementPending) return
      if (direction === 'tab') {
        // Add the new component as a tab in the target pane (no tree split).
        const newTabId = storeApi.getState().addTab(targetPanelId, placementPending.componentType)
        // Carry the pending panel state onto the new tab (duplicates and
        // state-seeded adds); mark loaded so registerPanel() doesn't
        // overwrite it with API data. Mirrors insertPanelAtTarget.
        const tabInitialState = placementPending.initialState
        if (tabInitialState) {
          storeApi.setState((s) => ({
            panels: { ...s.panels, [newTabId]: tabInitialState },
            panelLoaded: { ...s.panelLoaded, [newTabId]: true },
          }))
        } else {
          seedPanel(newTabId, placementPending.componentType)
        }
        setPlacementPending(null)
        return
      }
      if (direction === 'replace') {
        // Replace only the pane's ACTIVE tab, preserving the others. The store
        // handles the tab-group bookkeeping (and the tree leaf swap when the
        // host-leaf tab is active) and returns the replaced panel id so we can
        // drop its panel-scoped refs.
        const before = storeApi.getState().windowConfigs
        const replacedId = storeApi
          .getState()
          .replaceActiveTab(
            targetPanelId,
            placementPending.componentType,
            placementPending.initialState,
          )
        if (replacedId && !placementPending.initialState) {
          // The store mints the replacement's id and returns only the one it
          // dropped; the new id is the config that was not there before.
          const newId = Object.keys(storeApi.getState().windowConfigs).find((id) => !(id in before))
          if (newId) seedPanel(newId, placementPending.componentType)
        }
        if (replacedId) {
          delete panelActionHandlersRef.current[replacedId]
          const { [replacedId]: _o, ...restOverrides } = panelActionOverridesRef.current
          panelActionOverridesRef.current = restOverrides
          const { [replacedId]: _b, ...restBadges } = panelActionBadgesRef.current
          panelActionBadgesRef.current = restBadges
        }
        setPlacementPending(null)
        return
      }
      insertPanelAtTarget(
        placementPending.componentType,
        placementPending.initialState,
        targetPanelId,
        direction,
      )
      setPlacementPending(null)
    },
    [movePending, placementPending, insertPanelAtTarget, storeApi, defaultComponentType, seedPanel],
  )

  const cancelPlacement = useCallback(() => {
    setPlacementPending(null)
    setMovePending(null)
  }, [])

  const removePanel = useCallback(
    (id: string) => {
      if (locked) return
      // Grid-only panel (not a mosaic leaf — e.g. added to the bottom of a
      // scroll layout): remove it from the grid + configs WITHOUT rebuilding the
      // mosaic tree. The old code would set the whole tree to null here.
      if (!collectLeaves(storeApi.getState().mosaicTree).includes(id)) {
        storeApi.getState().removeGridPanel(id)
        delete panelActionHandlersRef.current[id]
        const { [id]: _o, ...restOverrides } = panelActionOverridesRef.current
        panelActionOverridesRef.current = restOverrides
        const { [id]: _b, ...restBadges } = panelActionBadgesRef.current
        panelActionBadgesRef.current = restBadges
        setActivePanel((cur) => (cur === id ? null : cur))
        return
      }
      // If this pane hosts a tab group, close its extra tabs first so their
      // panel state/configs are cleaned up and the group entry is removed.
      const group = storeApi.getState().tabGroups[id]
      if (group) {
        for (const tabId of group.tabs) {
          if (tabId !== id) storeApi.getState().closeTab(id, tabId)
        }
      }
      const { mosaicTree: tree, windowConfigs, updateLayout } = storeApi.getState()
      if (!tree) return

      let newTree: MosaicNode<string> | null = null
      if (typeof tree === 'string') {
        newTree = null
      } else {
        const leafPaths = new Map<string, MosaicBranch[]>()
        function walk(node: MosaicNode<string>, path: MosaicBranch[]) {
          if (typeof node === 'string') {
            leafPaths.set(node, path)
          } else {
            walk(node.first, [...path, 'first'])
            walk(node.second, [...path, 'second'])
          }
        }
        walk(tree, [])
        const targetPath = leafPaths.get(id)
        if (!targetPath || targetPath.length === 0) {
          newTree = null
        } else {
          const update = createRemoveUpdate(tree, targetPath)
          newTree = updateTree(tree, [update])
        }
      }

      const removedType = windowConfigs[id]?.componentType
      const { [id]: _removed, ...restConfigs } = windowConfigs
      const currentGridLayout = storeApi.getState().gridLayout
      const nextGridLayout = removeGridCell(currentGridLayout, id)
      updateLayout(newTree, restConfigs)
      if (nextGridLayout !== currentGridLayout) {
        storeApi.getState().setGridLayout(nextGridLayout)
      }
      storeApi.getState().cleanupPanel(id)
      if (removedType) {
        trackEvent(EVENTS.PANEL_REMOVED, { component_type: removedType })
      }
      delete panelActionHandlersRef.current[id]
      const { [id]: _, ...restOverrides } = panelActionOverridesRef.current
      panelActionOverridesRef.current = restOverrides
      setActivePanel((cur) => (cur === id ? null : cur))
    },
    [storeApi, locked],
  )

  // Uncontrolled mosaic: onChange is a no-op (Mosaic manages its own tree
  // during drag/resize). onRelease fires once when the user finishes the
  // interaction and we persist the final tree to the store.
  const handleMosaicChange = useCallback(() => {}, [])

  const handleMosaicRelease = useCallback(
    (newValue: MosaicNode<string> | null) => {
      // A locked (preset) layout keeps its panels but the user sizes them: take
      // a divider drag, refuse anything structural.
      if (locked && !isResizeOnly(storeApi.getState().mosaicTree, newValue)) return
      const { windowConfigs: configs, tabGroups, updateLayout } = storeApi.getState()
      if (newValue) {
        // Valid panel IDs = tree leaves PLUS tab-member panels (extra tabs are
        // owned by a tab group, not present in the mosaic tree). Without adding
        // the tab members here, dragging a pane would prune their windowConfigs
        // and the tab groups would lose all their tabs.
        const activeLeaves = new Set(getLeaves(newValue))
        for (const group of Object.values(tabGroups)) {
          for (const tabId of group.tabs) activeLeaves.add(tabId)
        }
        const next = { ...configs }
        let changed = false
        for (const key of Object.keys(next)) {
          if (!activeLeaves.has(key)) {
            delete next[key]
            changed = true
          }
        }
        updateLayout(newValue, changed ? next : configs)
      } else {
        updateLayout(null, {})
      }
    },
    [storeApi, locked],
  )

  // ─── Stable refs for callbacks used in effects ───

  const addPanelRef = useRef(addPanel)
  addPanelRef.current = addPanel

  const insertPanelAtTargetRef = useRef(insertPanelAtTarget)
  insertPanelAtTargetRef.current = insertPanelAtTarget

  // ─── Escape key to cancel placement mode ───

  useEffect(() => {
    if (!placementPending && !movePending) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlacementPending(null)
        setMovePending(null)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [placementPending, movePending])

  // ─── Check for pending panel import (from Import Profile modal) ───

  useEffect(() => {
    if (!layoutLoaded) return

    const checkPendingPanelImport = () => {
      const raw = sessionStorage.getItem('edge-pending-panel-import')
      if (!raw) return
      try {
        const { targetScope, targetLayoutId, componentType, panelState, subPanels, name } =
          JSON.parse(raw) as {
            targetScope?: ShareScope
            targetLayoutId: string
            componentType: string
            panelState: Record<string, unknown>
            subPanels?: Record<string, Record<string, unknown>>
            name: string
          }
        if ((targetScope ?? 'dashboard') !== shareScope) return
        if (locked) return
        // Only consume the import if this is the target layout
        if (!targetLayoutId || targetLayoutId !== layoutId) return
        // Bypass placement mode: insert directly at TOP_RIGHT
        const importedPanelId = insertPanelAtTargetRef.current(
          componentType as ComponentType,
          panelState,
        )
        if (!importedPanelId) return
        if (subPanels) {
          const imported = Object.fromEntries(
            Object.entries(subPanels)
              .filter(([suffix]) => suffix.startsWith('::'))
              .map(([suffix, state]) => [`${importedPanelId}${suffix}`, state]),
          )
          const loaded = Object.fromEntries(Object.keys(imported).map((key) => [key, true]))
          storeApi.setState((state) => ({
            panels: { ...state.panels, ...imported },
            panelLoaded: { ...state.panelLoaded, ...loaded },
          }))
        }
        sessionStorage.removeItem('edge-pending-panel-import')
        useNotificationStore.getState().showSuccess(`Panel "${name}" imported successfully`)
      } catch {
        sessionStorage.removeItem('edge-pending-panel-import')
      }
    }

    // Check on mount (after layout loaded)
    checkPendingPanelImport()

    // Also listen for event (when already mounted on dashboard)
    window.addEventListener('edge:pending-panel-import', checkPendingPanelImport)
    return () => window.removeEventListener('edge:pending-panel-import', checkPendingPanelImport)
  }, [layoutLoaded, layoutId, locked, shareScope, storeApi])

  // ─── Keyboard shortcut: Ctrl+Backspace to remove active panel ───

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && (e.ctrlKey || e.metaKey)) {
        const t = e.target as HTMLElement
        const tag = t.tagName
        // contentEditable covers CodeMirror (the script dock) and the notebook editor,
        // where Cmd+Backspace is "delete to line start", not "remove this panel".
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) return
        const id = activePanelRef.current
        if (id) {
          e.preventDefault()
          removePanel(id)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [removePanel])

  // ─── Mobile ticker select ref (populated by MobileWorkspace) ───

  const mobileSelectTickerRef = useRef<((ticker: string) => void) | null>(null)

  // ─── Context value ───

  const isPlacementMode = placementPending !== null || movePending !== null

  const isPanelHubOpen = panelHubOpen && !locked

  const contextValue = useMemo<WorkspaceMosaicContextValue>(
    () => ({
      addPanel,
      isPlacementMode,
      cancelPlacement,
      isPanelHubOpen,
      togglePanelHub,
      closePanelHub,
    }),
    [addPanel, isPlacementMode, cancelPlacement, isPanelHubOpen, togglePanelHub, closePanelHub],
  )

  // Per-type counts for the hub's limit badges (the header computes the same
  // for its dropdown; the hub lives in here so it reads the store directly).
  const panelCounts = useMemo(() => {
    const counts: Partial<Record<ComponentType, number>> = {}
    for (const cfg of Object.values(windowConfigs)) {
      counts[cfg.componentType] = (counts[cfg.componentType] ?? 0) + 1
    }
    return counts
  }, [windowConfigs])

  const panelHub = isPanelHubOpen ? (
    <PanelHub
      availableTypes={availableTypes}
      panelCounts={panelCounts}
      onAdd={addPanel}
      onDrop={dropPanel}
      onClose={closePanelHub}
      dragDisabled={isPlacementMode || layoutMode === 'grid'}
      suspendEsc={isPlacementMode}
      grouped={panelHubGrouped}
    />
  ) : null

  // ─── Duplicate panel handler ───

  const handleDuplicatePanel = useCallback(
    (panelId: string, config: WindowConfig) => {
      const state = storeApi.getState()
      const panelState = state.panels[panelId]
      // A duplicate must land somewhere NEW (split or tab): replacing a panel
      // with a copy of itself is meaningless, so the overlay hides Replace.
      addPanel(config.componentType, panelState ? { ...panelState } : undefined, {
        hideReplace: true,
      })
    },
    [storeApi, addPanel],
  )

  // ─── Export panel handler ───

  const handleExportPanel = useCallback(
    async (panelId: string, config: WindowConfig) => {
      if (!shareScope) return
      setExportUrl('')
      setExportError('')
      setExportDialogOpen(true)
      exportRetryRef.current = () => void handleExportPanel(panelId, config)
      const state = storeApi.getState()
      const panelState = state.panels[panelId] ?? {}
      const subPanels = Object.fromEntries(
        Object.entries(state.panels)
          .filter(
            ([key, value]) => key.startsWith(`${panelId}::`) && value && typeof value === 'object',
          )
          .map(([key, value]) => [key.slice(panelId.length), value as Record<string, unknown>]),
      )
      const exportData = buildProfileExport(
        shareScope,
        `${config.componentType} panel`,
        config.componentType,
        {
          panelId,
          componentType: config.componentType,
          ...panelState,
        } as Record<string, unknown>,
        subPanels,
      )
      try {
        const url = await generateShareUrl(exportData)
        setExportUrl(url)
      } catch {
        setExportError('Could not create share link')
      }
    },
    [shareScope, storeApi],
  )

  const panelShareDialog = (
    <ShareLinkDialog
      open={exportDialogOpen}
      title="Share Panel"
      description="Copy this link and share it with others to import this panel."
      url={exportUrl}
      error={exportError}
      onRetry={() => exportRetryRef.current?.()}
      onClose={() => setExportDialogOpen(false)}
    />
  )

  // Don't render the mosaic until layout data is loaded from IDB and both
  // expression libraries have settled. Rendering with defaults would mount
  // throwaway components that fire network requests, and a request compiled
  // before the library arrives would drop every expression it references.
  // The header (passed as children) still renders so tabs are interactive.
  if (!layoutLoaded || !librariesReady) {
    return (
      <WorkspaceMosaicContext value={contextValue}>
        <div className="flex h-full flex-col">
          {children}
          <div className="flex min-h-0 flex-1 flex-row">
            {leftRailSlot}
            <div className="relative flex-1 min-w-0 min-h-0">
              {/* Skeleton mosaic (not a bare spinner) so the user sees layout
                  structure while the real panels load, even on a slow network. */}
              <WorkspaceSkeleton />
            </div>
          </div>
        </div>
      </WorkspaceMosaicContext>
    )
  }

  // ─── Mobile view ───

  if (isMobile) {
    return (
      <WorkspaceMosaicContext value={contextValue}>
        <MobileWorkspace
          registry={registry}
          symbols={symbols}
          onSymbolsChange={onSymbolsChange}
          selectTickerRef={mobileSelectTickerRef}
          onExportPanel={shareScope ? handleExportPanel : undefined}
        >
          {children}
        </MobileWorkspace>
        {panelShareDialog}
      </WorkspaceMosaicContext>
    )
  }

  // ─── Grid view (vertically-scrollable layoutMode) ───
  // A third renderer alongside mosaic/mobile: same registry + store + panels,
  // swapped into a scroll container instead of absolute-positioned tiles.

  if (layoutMode === 'grid') {
    return (
      <WorkspaceMosaicContext value={contextValue}>
        <div className="relative flex h-full flex-col">
          {children}
          <div className="flex min-h-0 flex-1 flex-row">
            {leftRailSlot}
            {/* scrollbar-gutter:stable reserves the 8px vertical scrollbar space
                permanently. Without it, when this container's scrollbar toggles
                (e.g. a reflow as a panel settings sheet opens) its clientWidth
                jumps 8px, and react-grid-layout — which measures this width and
                positions every tile with a transitioned `transform` — animates
                the whole grid sideways ("the background slides"). Reserving the
                gutter keeps the measured width constant so nothing moves. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {aboveContentScrolls ? null : aboveContentSlot}
              <div className="relative min-h-0 min-w-0 flex-1 overflow-y-auto bg-background [scrollbar-gutter:stable]">
                {aboveContentScrolls ? aboveContentSlot : null}
                <GridWorkspace
                  fitContent={gridFitContent}
                  fixed={gridFixed}
                  clickToInteract={gridClickToInteract}
                  renderHeader={(panelId, config) => {
                    // The header this layer would render by default. Built as a
                    // local so a feature override can return it unchanged when it
                    // decides not to substitute (a decision that can depend on
                    // panel state the plain override function can't read).
                    const defaultHeader = (
                      <PanelHeader
                        panelId={panelId}
                        windowConfig={config}
                        symbols={symbols}
                        onSymbolChange={(idx) => handleSymbolChange(panelId, idx)}
                        onSelectTicker={(ticker) => handleSelectTicker(panelId, ticker)}
                        onLinkModeChange={(mode) => handleLinkModeChange(panelId, mode)}
                        onToggleHeaderVisible={() => handleToggleHeaderVisible(panelId)}
                        onDelete={() => removePanel(panelId)}
                        onExport={shareScope ? () => handleExportPanel(panelId, config) : undefined}
                        onDuplicate={() => handleDuplicatePanel(panelId, config)}
                        onAction={(actionId, anchor) =>
                          panelActionHandlersRef.current[panelId]?.(actionId, anchor)
                        }
                        actionOverrides={panelActionOverridesRef.current[panelId]}
                        actionBadges={panelActionBadgesRef.current[panelId]}
                        locked={locked}
                        focusDate={focusDate}
                        onFocusDate={(d) => {
                          setFocusDate(d)
                          setFocusDateSource(d ? config.componentType : null)
                        }}
                        onResetFocusDate={() => {
                          setFocusDate(null)
                          setFocusDateSource(null)
                        }}
                        focusDateSource={focusDateSource}
                        tickerCleared={config.tickerCleared}
                        onClearTicker={
                          config.componentType === 'news'
                            ? () => handleClearTicker(panelId)
                            : undefined
                        }
                        rightExtras={renderToolbarExtrasRight?.(panelId, config, (actionId) =>
                          panelActionHandlersRef.current[panelId]?.(actionId),
                        )}
                        onAskAI={askAIHandler(panelId, config.componentType)}
                      >
                        {renderToolbarExtras?.(panelId, config, (actionId) =>
                          panelActionHandlersRef.current[panelId]?.(actionId),
                        )}
                      </PanelHeader>
                    )
                    // Feature pages can swap in a custom minimal header per panel
                    // type (e.g. the history scorecard) — keeps this workspace
                    // layer feature-agnostic, mirroring the registry injection.
                    return (
                      renderGridHeaderOverride?.(
                        panelId,
                        config,
                        {
                          triggerPanelAction: (actionId: string) =>
                            panelActionHandlersRef.current[panelId]?.(actionId),
                          onDelete: () => removePanel(panelId),
                          onExport: () => handleExportPanel(panelId, config),
                          onDuplicate: () => handleDuplicatePanel(panelId, config),
                          onAskAI: askAIHandler(panelId, config.componentType),
                          layoutMode: 'grid',
                        },
                        defaultHeader,
                      ) ?? defaultHeader
                    )
                  }}
                  renderBody={(panelId, config) => {
                    const PanelComp = registry[config.componentType]
                    // Outline-only pass (deferred mode). Blank on purpose: anything
                    // drawn here reads as panel content.
                    if (!panelsReady) return <div className="h-full w-full" aria-hidden="true" />
                    return (
                      <PanelComp
                        panelId={panelId}
                        windowConfig={config}
                        symbols={symbols}
                        focusDate={focusDate}
                        focusDateSource={focusDateSource}
                        onFocusDate={(d) => {
                          setFocusDate(d)
                          setFocusDateSource(d ? config.componentType : null)
                        }}
                        onSelectTicker={(t, date, opts) =>
                          handleSelectTicker(panelId, t, date, opts)
                        }
                        onClearSelection={() => handleClearSelection(panelId)}
                        onRegisterToolbarHandler={(handler) => {
                          panelActionHandlersRef.current[panelId] = handler
                        }}
                        onRegisterPanelActions={(actionIds) => {
                          const existing = panelActionOverridesRef.current[panelId]
                          if (
                            existing &&
                            existing.length === actionIds.length &&
                            existing.every((a, i) => a === actionIds[i])
                          )
                            return
                          panelActionOverridesRef.current = {
                            ...panelActionOverridesRef.current,
                            [panelId]: actionIds,
                          }
                          if (!pendingActionUpdate.current) {
                            pendingActionUpdate.current = true
                            requestAnimationFrame(() => {
                              pendingActionUpdate.current = false
                              setPanelActionVersion((v) => v + 1)
                            })
                          }
                        }}
                        onRegisterActionBadges={(badges) => {
                          panelActionBadgesRef.current = {
                            ...panelActionBadgesRef.current,
                            [panelId]: badges,
                          }
                          if (!pendingActionUpdate.current) {
                            pendingActionUpdate.current = true
                            requestAnimationFrame(() => {
                              pendingActionUpdate.current = false
                              setPanelActionVersion((v) => v + 1)
                            })
                          }
                        }}
                      />
                    )
                  }}
                />
              </div>
            </div>
          </div>
          {panelHub}
          {panelShareDialog}
        </div>
      </WorkspaceMosaicContext>
    )
  }

  // ─── Desktop mosaic view ───

  return (
    <WorkspaceMosaicContext value={contextValue}>
      <div className="relative flex h-full flex-col">
        {/* Children (e.g. DashboardHeader) render above the mosaic */}
        {children}

        <div className="flex min-h-0 flex-1 flex-row">
          {leftRailSlot}
          <div className="flex min-w-0 flex-1 flex-col">
            {aboveContentSlot}
            <div className="relative min-h-0 min-w-0 flex-1">
              {/* Floating placement hint banner */}
              {placementPending && (
                <div className="absolute left-1/2 top-3 z-100 -translate-x-1/2 rounded-lg border border-primary/30 bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-[0_4px_24px_rgba(0,0,0,0.3)] animate-in fade-in slide-in-from-top-2 duration-200">
                  Click an arrow to place{' '}
                  <span className="font-semibold">
                    {COMPONENT_META[placementPending.componentType]?.label ??
                      placementPending.componentType}
                  </span>{' '}
                  &mdash; Press{' '}
                  <Kbd className="border border-primary-foreground/30 bg-primary-foreground/15 text-primary-foreground">
                    Esc
                  </Kbd>{' '}
                  to cancel
                </div>
              )}
              {/* Floating move hint banner */}
              {movePending && (
                <div className="absolute left-1/2 top-3 z-100 -translate-x-1/2 rounded-lg border border-primary/30 bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-[0_4px_24px_rgba(0,0,0,0.3)] animate-in fade-in slide-in-from-top-2 duration-200">
                  Choose where to move{' '}
                  <span className="font-semibold">
                    {COMPONENT_META[windowConfigs[movePending.panelId]?.componentType ?? 'chart']
                      ?.label ?? 'tab'}
                  </span>{' '}
                  &mdash; arrows split, center adds as a tab. Press{' '}
                  <Kbd className="border border-primary-foreground/30 bg-primary-foreground/15 text-primary-foreground">
                    Esc
                  </Kbd>{' '}
                  to cancel
                </div>
              )}
              {/* Empty-layout state. react-mosaic renders NOTHING when its tree is
                null (deleting the last panel → blank), so this overlay invites
                adding the first panel. `addPanel` self-inserts into the CURRENT
                layout when the tree is empty (no placement gesture) — so it
                populates this layout in place rather than spawning a new one.
                Shared renderer → covers both the dashboard and history pages. */}
              {isEmptyLayout && layoutLoaded && (
                // Solid `bg-background` — react-mosaic's empty root paints WHITE,
                // which is what turned the panel area blank; this covers it so the
                // empty layout reads as the dark app surface.
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background text-center">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">This layout is empty</p>
                    <p className="text-xs text-muted-foreground">Add a panel to get started.</p>
                  </div>
                  {!locked && <AddPanelMenu onAdd={addPanel} availableTypes={availableTypes} />}
                </div>
              )}
              <MosaicWithoutDragDropContext
                renderTile={(id: string, path: MosaicBranch[]) => {
                  // A pane (mosaic leaf `id`) may host a tab group. The host is
                  // tabs[0]; extra tabs are non-leaf panel IDs. Component-scoped
                  // chrome (header, actions, body) follows the ACTIVE tab; pane-
                  // scoped ops (delete, activation, placement, tree path) use `id`.
                  const group = tabGroups[id]
                  const tabIds = group
                    ? group.tabs.filter((t) => t === id || !!windowConfigs[t])
                    : [id]
                  const hasTabs = !!group && tabIds.length >= 2
                  const activeId =
                    group && tabIds.includes(group.activeTabId) ? group.activeTabId : id
                  // Move needs somewhere else to go: another pane (a non-root
                  // path, read fresh from mosaic) or a sibling tab to leave behind.
                  const canMove = !locked && (hasTabs || path.length > 0)
                  // Moving a whole single-panel pane: it can't land on itself.
                  const isMoveSource =
                    movePending?.sourceHostId === id && movePending.panelId === id && !hasTabs
                  const config = windowConfigs[activeId] ??
                    windowConfigs[id] ?? {
                      symbolIndex: 0,
                      componentType: defaultComponentType,
                    }
                  const PanelComp = registry[config.componentType]
                  // History mode = broadcast focus date OR the panel's own calendar
                  // date. Chart-family panels only (they're the ones that rewind).
                  //
                  // History-page panels use a DIFFERENT rule: their baseline is the
                  // reviewed record's date (already historical), so the plain
                  // "focusDate is historical" arm would light every record. Instead
                  // they go yellow only once a drill deviates from that record date
                  // (deviatedIds). Multi-chart history drills are pinned to the
                  // record date and never deviate, so this is effectively the
                  // single history-chart panel — harmless to include both.
                  // The backtest trade chart is the same case: its baseline is
                  // the clicked trade's day, always historical.
                  const isHistoryType =
                    config.componentType === 'history-chart' ||
                    config.componentType === 'history-multi-chart' ||
                    config.componentType === 'backtest-trade-chart'
                  const showFocusBorder = isHistoryType
                    ? deviatedIds.has(activeId)
                    : (config.componentType === 'chart' ||
                        config.componentType === 'multi-chart' ||
                        config.componentType === 'watchlist') &&
                      (historicalFocusDate(linkedFocusDate(config, focusDate)) != null ||
                        localFocusIds.has(activeId))
                  const showDrillBorder = !!drillOpen[activeId]
                  const showReplayBorder = !!replayOpen[activeId]
                  const showDrillSourceBorder = drillSourceIds.has(activeId)

                  return (
                    <MosaicWindow
                      path={path}
                      title=""
                      className={
                        [
                          // Replay (orange) wins over history (yellow), which wins
                          // over drill (purple) — class order here is cosmetic; the
                          // actual precedence is CSS source order (see
                          // mosaic-overrides.css: drill, then focus-date, then replay).
                          showReplayBorder && 'mosaic-window--replay',
                          showDrillBorder && 'mosaic-window--drill',
                          showFocusBorder && 'mosaic-window--focus-date',
                          showDrillSourceBorder && 'mosaic-window--drill-source',
                          config.headerVisible === false && 'mosaic-window--header-collapsed',
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      draggable={!locked && !placementPending && !movePending}
                      renderToolbar={() => {
                        const defaultHeader = (
                          <PanelHeader
                            panelId={activeId}
                            windowConfig={config}
                            symbols={symbols}
                            onSymbolChange={(idx) => handleSymbolChange(activeId, idx)}
                            onSelectTicker={(ticker) => handleSelectTicker(activeId, ticker)}
                            onLinkModeChange={(mode) => handleLinkModeChange(activeId, mode)}
                            onToggleHeaderVisible={() => handleToggleHeaderVisible(activeId)}
                            onDelete={() => removePanel(id)}
                            onExport={
                              shareScope ? () => handleExportPanel(activeId, config) : undefined
                            }
                            onDuplicate={() => handleDuplicatePanel(activeId, config)}
                            onMove={
                              canMove
                                ? () => setMovePending({ panelId: activeId, sourceHostId: id })
                                : undefined
                            }
                            moveLabel={hasTabs ? 'Move tab' : 'Move'}
                            onAction={(actionId, anchor) =>
                              panelActionHandlersRef.current[activeId]?.(actionId, anchor)
                            }
                            actionOverrides={panelActionOverridesRef.current[activeId]}
                            actionBadges={panelActionBadgesRef.current[activeId]}
                            locked={locked}
                            focusDate={focusDate}
                            onFocusDate={(d) => {
                              setFocusDate(d)
                              setFocusDateSource(d ? config.componentType : null)
                            }}
                            onResetFocusDate={() => {
                              setFocusDate(null)
                              setFocusDateSource(null)
                            }}
                            focusDateSource={focusDateSource}
                            tickerCleared={config.tickerCleared}
                            onClearTicker={
                              config.componentType === 'news'
                                ? () => handleClearTicker(activeId)
                                : undefined
                            }
                            rightExtras={renderToolbarExtrasRight?.(activeId, config, (actionId) =>
                              panelActionHandlersRef.current[activeId]?.(actionId),
                            )}
                            onAskAI={askAIHandler(activeId, config.componentType)}
                          >
                            {renderToolbarExtras?.(activeId, config, (actionId) =>
                              panelActionHandlersRef.current[activeId]?.(actionId),
                            )}
                          </PanelHeader>
                        )
                        // Same override the grid path consults — mosaic ignored it
                        // before, so scorecard's title header never appeared here.
                        const header =
                          renderGridHeaderOverride?.(
                            activeId,
                            config,
                            {
                              triggerPanelAction: (actionId: string) =>
                                panelActionHandlersRef.current[activeId]?.(actionId),
                              onDelete: () => removePanel(id),
                              onExport: () => handleExportPanel(activeId, config),
                              onDuplicate: () => handleDuplicatePanel(activeId, config),
                              onAskAI: askAIHandler(activeId, config.componentType),
                              layoutMode: 'mosaic',
                            },
                            defaultHeader,
                          ) ?? defaultHeader
                        return (
                          // React DnD connectors need a native element root — the
                          // component wrapper below cannot receive the drag ref.
                          <div className="flex w-full">
                            <PanelToolbarWrapper headerVisible={config.headerVisible}>
                              {header}
                            </PanelToolbarWrapper>
                          </div>
                        )
                      }}
                    >
                      <PanelWrapper
                        panelId={id}
                        panelType={config.componentType}
                        isActive={activePanel === id}
                        onActivate={() => setActivePanel(id)}
                      >
                        <div className="flex h-full w-full">
                          <div className="relative min-w-0 flex-1">
                            {!panelsReady ? (
                              <div className="h-full w-full" aria-hidden="true" />
                            ) : (
                              <PanelComp
                                key={activeId}
                                panelId={activeId}
                                windowConfig={config}
                                symbols={symbols}
                                focusDate={focusDate}
                                focusDateSource={focusDateSource}
                                onFocusDate={(d) => {
                                  setFocusDate(d)
                                  setFocusDateSource(d ? config.componentType : null)
                                }}
                                onSelectTicker={(t, date, opts) =>
                                  handleSelectTicker(activeId, t, date, opts)
                                }
                                onClearSelection={() => handleClearSelection(activeId)}
                                onRegisterToolbarHandler={(handler) => {
                                  panelActionHandlersRef.current[activeId] = handler
                                }}
                                onRegisterPanelActions={(actionIds) => {
                                  const existing = panelActionOverridesRef.current[activeId]
                                  if (
                                    existing &&
                                    existing.length === actionIds.length &&
                                    existing.every((a, i) => a === actionIds[i])
                                  )
                                    return
                                  panelActionOverridesRef.current = {
                                    ...panelActionOverridesRef.current,
                                    [activeId]: actionIds,
                                  }
                                  if (!pendingActionUpdate.current) {
                                    pendingActionUpdate.current = true
                                    requestAnimationFrame(() => {
                                      pendingActionUpdate.current = false
                                      setPanelActionVersion((v) => v + 1)
                                    })
                                  }
                                }}
                                onRegisterActionBadges={(badges) => {
                                  panelActionBadgesRef.current = {
                                    ...panelActionBadgesRef.current,
                                    [activeId]: badges,
                                  }
                                  if (!pendingActionUpdate.current) {
                                    pendingActionUpdate.current = true
                                    requestAnimationFrame(() => {
                                      pendingActionUpdate.current = false
                                      setPanelActionVersion((v) => v + 1)
                                    })
                                  }
                                }}
                              />
                            )}
                            {(placementPending || movePending) && !isMoveSource && (
                              <PlacementOverlay
                                panelId={id}
                                onPlace={(direction) => handlePlacement(id, direction)}
                                showReplace={movePending != null || !placementPending?.hideReplace}
                              />
                            )}
                            {isMoveSource && (
                              // The pane being moved: scrim only, no targets.
                              <div className="absolute inset-0 z-50 bg-background/50" />
                            )}
                          </div>
                          {hasTabs && (
                            <PaneTabStrip
                              tabIds={tabIds}
                              activeTabId={activeId}
                              hostId={id}
                              windowConfigs={windowConfigs}
                              availableTypes={availableTypes}
                              onSelect={(tabId) => storeApi.getState().setActiveTab(id, tabId)}
                              onClose={(tabId) => storeApi.getState().closeTab(id, tabId)}
                              onAddTab={(type) =>
                                seedPanel(storeApi.getState().addTab(id, type), type)
                              }
                              onMoveTab={(tabId) =>
                                setMovePending({ panelId: tabId, sourceHostId: id })
                              }
                            />
                          )}
                        </div>
                      </PanelWrapper>
                    </MosaicWindow>
                  )
                }}
                initialValue={storeApi.getState().mosaicTree}
                onChange={handleMosaicChange}
                onRelease={handleMosaicRelease}
                className="mosaic-blueprint-theme bp5-dark"
                mosaicId={WORKSPACE_MOSAIC_ID}
              />
            </div>
          </div>
        </div>
        {/* Overlay (scrim + sheet) over the whole workspace, header included,
            so the sheet runs the full height; it never reflows the tiles. */}
        {panelHub}

        {panelShareDialog}
      </div>
    </WorkspaceMosaicContext>
  )
}
